/** Which job the logger core is doing right now (PE-22 slice A).
 *
 *  Before this contract the distinction lived as a boolean named
 *  `isPastSessionEdit`, re-derived independently in six places. It carried
 *  TWO concerns at once, and prescription needs one of them without the
 *  other:
 *
 *  - the CLOCK — where a completion timestamp comes from
 *  - the LIVE CUES: rest countdown, bodyweight marker, editable controls
 *
 *  | mode        | clock                    | live cues |
 *  |-------------|--------------------------|-----------|
 *  | 'log'       | the wall clock, now      | on        |
 *  | 'log-past'  | placed inside the window | off       |
 *  | 'prescribe' | none — nothing happened  | off       |
 *
 *  So the two questions are asked separately: the clock asks
 *  `mode === 'log-past'`, the live cues ask `mode !== 'log'`. Collapsing
 *  them back into one boolean is what made a prescribed set look performed.
 *
 *  `'prescribe'` has NO producer yet — `deriveLoggerMode` never returns it.
 *  It arrives with slices C/D, which is why today's behaviour is unchanged
 *  bit for bit. */
export type LoggerMode = 'log' | 'log-past' | 'prescribe';

/** The single derivation of the mode from the editing target.
 *
 *  `editing.mode` is written explicitly only for 'repeat'; a plain
 *  edit-from-history creates the target WITHOUT the field (the read-time
 *  default fills it as 'edit'), so a missing mode means edit.
 *
 *  A REPEAT is live logging: it clones the structure into a brand-new
 *  session with a fresh `startedAt`, so the athlete is training now. Only a
 *  true edit is correcting history. */
export const deriveLoggerMode = (
  /** Structural param, not the LoggerScreen `EditingTarget` import: an
   *  EditingTarget satisfies it, and the predicate stays pure and unit-
   *  testable without a cast in the double. Same reasoning that already
   *  governed shouldPersistDraft. */
  editing: { mode?: 'edit' | 'repeat' } | null,
): LoggerMode => (editing != null && editing.mode !== 'repeat' ? 'log-past' : 'log');
